import useAppQueryParam from "@/hooks/useAppQueryParam";
import useGetUserToken from "@/lib/auth/useGetUserToken";
import { createLogger } from "@/utils/createLogger";

import { IS_DEV } from "@/data/constants";
import {
  PLAN_REF_ID_MAPPING,
  PLAN_STATUS,
  PLANS_IN_BUNDLE,
} from "@/data/plans";
import { TConfiguredTracker } from "@/tracking/createTrackerConfig";
import { DEFAULT_PRICING_VERSION } from "../Dashboard/v2/constants.dashboard";
import { CHECKOUT_COPY_CONFIG } from "./config.checkout";
import { CHECKOUT_TYPE } from "./constants.checkout";
import cancelCompletePlanCardContent from "./data/content/en/cancel-complete-plan-card.json";
import cancelPlanCardContent from "./data/content/en/cancel-plan-card.json";
import checkoutCartContent from "./data/content/en/checkout-cart.json";
import managePlansSectionContent from "./data/content/en/manage-plans-section.json";
import offerUnavailableToastContent from "./data/content/en/offer-unavailable-toast.json";
import switchIntervalCardContent from "@/components/shared/interval-switch/content/en/switch-interval-card.json";
import { TCheckoutEvent } from "./events.checkout";
import {
  TCheckoutCartConfig,
  TCheckoutContent,
  TCheckoutType,
  TCheckoutVariant,
} from "./types.checkout";
import { getCheckoutVariant } from "./utils/getCheckoutType";
import { isSupportedCheckoutType } from "./utils/isSupportedCheckoutType";

// ─── Types ───────────────────────────────────────────────────────────────────

type TCheckoutServiceDeps = {
  getQueryParam: ReturnType<typeof useAppQueryParam>["getQueryParam"];
  userToken: ReturnType<typeof useGetUserToken>;
  manualSelection: boolean;
  track: TConfiguredTracker<TCheckoutEvent>;
  isV1?: boolean;
};

/**
 * Factory that produces the {@link CheckoutService} singleton.
 *
 * ## Pattern
 * Closure-based dependency injection. All infrastructure values (router, auth,
 * query client, tracker) are resolved at the React layer and injected once via
 * `init()`. After that, any module can `import { CheckoutService }` and read
 * the values without going through React hooks.
 *
 * ## Lifecycle
 * 1. `CheckoutService` is created at module load (empty state).
 * 2. `index.tsx` calls `CheckoutService.init(deps)` on first render, passing
 *    all hook-derived values.
 * 3. Every getter (`checkoutType`, `Config`, `track`, …) lazily reads the
 *    closure variable set by `init()` — so they always reflect the initialized
 *    state, not the empty default.
 *
 * @internal Use {@link CheckoutService} — do not call this directly.
 */
function createCheckoutService() {
  // private state
  let _checkoutType: TCheckoutType = CHECKOUT_TYPE.MANAGE;
  let _backRedirect: string | null = null;
  let _manualSelection = false;
  let _userToken: ReturnType<typeof useGetUserToken> = undefined;
  let _track: TConfiguredTracker<TCheckoutEvent>;
  let _Config: {
    activeConfig: (typeof CHECKOUT_COPY_CONFIG)[TCheckoutType];
    cartConfig: TCheckoutCartConfig;
    checkoutVariant: TCheckoutVariant;
  };
  let _enabledLogging = IS_DEV;

  const _Constants = {
    PLAN_STATUS,
    PLAN_REF_ID_MAPPING,
    PLANS_IN_BUNDLE,
    DEFAULT_PRICING_VERSION,
  };

  const _Content: TCheckoutContent = {
    ...cancelPlanCardContent,
    ...cancelCompletePlanCardContent,
    ...checkoutCartContent,
    ...managePlansSectionContent,
    ...switchIntervalCardContent,
    ...offerUnavailableToastContent,
  };

  function init(deps: TCheckoutServiceDeps) {
    const { getQueryParam, userToken, manualSelection, track, isV1 } = deps;

    _userToken = userToken;

    const param = getQueryParam("type");
    const defaultType = isV1 ? CHECKOUT_TYPE.MIGRATION : CHECKOUT_TYPE.MANAGE;
    _checkoutType = isSupportedCheckoutType(param) ? param : defaultType;
    _backRedirect = getQueryParam("back");
    _manualSelection = manualSelection;
    _track = track;

    const activeConfig = CHECKOUT_COPY_CONFIG[_checkoutType];

    _Config = {
      activeConfig,
      cartConfig: activeConfig.cart as TCheckoutCartConfig,
      checkoutVariant: getCheckoutVariant(_checkoutType),
    };
  }

  return {
    init,
    get checkoutType() {
      return _checkoutType;
    },
    get backRedirect() {
      return _backRedirect;
    },
    get manualSelection() {
      return _manualSelection;
    },
    get userToken() {
      return _userToken;
    },
    get Config() {
      return _Config;
    },
    get track() {
      return _track;
    },
    get Constants() {
      return _Constants;
    },
    get Content() {
      return _Content;
    },
    /** Module logger — available before `init()` is called. */
    logger: createLogger("CHECKOUT", _enabledLogging),
  };
}

/**
 * Singleton service for the Checkout module.
 *
 * Must be initialized once via `CheckoutService.init(deps)` in `index.tsx`
 * before any consumer reads from it.
 *
 * **Reading values** (after init):
 * ```ts
 * import { CheckoutService } from "./service.checkout";
 *
 * CheckoutService.checkoutType       // "plan_manage" | "plan_purchase" | ...
 * CheckoutService.Config.activeConfig
 * CheckoutService.Content.cancelPlanList
 * CheckoutService.Handlers.goBack()
 * CheckoutService.track(...)
 * ```
 */
export const CheckoutService = createCheckoutService();

export type TCheckoutService = typeof CheckoutService;
